import { supabaseRequest, type SupabaseSession } from "../../../lib/supabase";
import type { MarketAnalysis, MarketAnalysisComparable } from "../domain/types";
import { normalizeMarketAnalyses } from "../domain/validation";
import {
  locationMetadataFromSourceSnapshot,
  marketMetadataFromSourceSnapshot,
  propertyModelFromSourceSnapshot,
  sourceSnapshotWithLocationMetadata,
  sourceSnapshotWithMarketMetadata,
  sourceSnapshotWithPropertyModel,
  sourceSnapshotWithoutLocationMetadata
} from "../location/locationMetadata";

type MarketAnalysisRow = {
  id: string;
  user_id: string;
  case_id: string;
  version: number;
  status: "draft" | "finalized";
  subject_snapshot: unknown;
  result_snapshot: unknown;
  market_funnel_snapshot: unknown;
  strategy_snapshot: unknown;
  methodology_version: string;
  source_cutoff_at: string;
  snapshot_hash: string | null;
  created_at: string;
  updated_at: string;
  finalized_at: string | null;
};

type MarketComparableRow = {
  id: string;
  analysis_id: string;
  user_id: string;
  sort_order: number;
  city: string;
  locality: string;
  street: string;
  property_type: string;
  rooms: string;
  area: number | null;
  condition: string;
  reconstruction: string;
  electricity: string;
  core_type: string;
  kitchen: string;
  balcony_type: string;
  cellar: string;
  floor: number | null;
  elevator: string;
  orientation: string;
  building_condition: string;
  asking_price: number | null;
  realized_price: number | null;
  listing_status: string;
  source_name: string;
  source_url: string;
  captured_at: string;
  source_snapshot: unknown;
  source_hash: string;
  similarity_score: number;
  score_breakdown: unknown;
  created_at: string;
  updated_at: string;
};

export type MarketAnalysisSyncResult = {
  analyses: MarketAnalysis[];
  session: SupabaseSession;
};

const timestamp = (value: string | undefined) => {
  const parsed = value ? Date.parse(value) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const analysisRow = (analysis: MarketAnalysis, statusOverride?: "draft" | "finalized") => ({
  id: analysis.id,
  user_id: analysis.userId,
  case_id: analysis.caseId,
  version: analysis.version,
  status: statusOverride ?? analysis.status,
  subject_snapshot: analysis.subjectSnapshot,
  result_snapshot: analysis.resultSnapshot ?? null,
  market_funnel_snapshot: analysis.marketFunnelSnapshot ?? null,
  strategy_snapshot: analysis.strategySnapshot,
  methodology_version: analysis.methodologyVersion,
  source_cutoff_at: analysis.sourceCutoffAt,
  snapshot_hash: statusOverride === "draft" ? null : analysis.snapshotHash ?? null,
  created_at: analysis.createdAt,
  updated_at: analysis.updatedAt,
  finalized_at: statusOverride === "draft" ? null : analysis.finalizedAt ?? null
});

const comparableRow = (comparable: MarketAnalysisComparable) => ({
  id: comparable.id,
  analysis_id: comparable.analysisId,
  user_id: comparable.userId,
  sort_order: comparable.sortOrder,
  city: comparable.city,
  locality: comparable.locality,
  street: comparable.street,
  property_type: comparable.propertyType,
  rooms: comparable.rooms,
  area: comparable.area ?? null,
  condition: comparable.condition,
  reconstruction: comparable.reconstruction,
  electricity: comparable.electricity,
  core_type: comparable.coreType,
  kitchen: comparable.kitchen,
  balcony_type: comparable.balconyType,
  cellar: comparable.cellar,
  floor: comparable.floor ?? null,
  elevator: comparable.elevator,
  orientation: comparable.orientation,
  building_condition: comparable.buildingCondition,
  asking_price: comparable.askingPrice ?? null,
  realized_price: comparable.realizedPrice ?? null,
  listing_status: comparable.listingStatus,
  source_name: comparable.sourceName,
  source_url: comparable.sourceUrl,
  captured_at: comparable.capturedAt,
  source_snapshot: sourceSnapshotWithMarketMetadata(
    sourceSnapshotWithPropertyModel(sourceSnapshotWithLocationMetadata(comparable.sourceSnapshot, {
      cityNormalized: comparable.cityNormalized,
      citySource: comparable.citySource,
      municipalityId: comparable.municipalityId,
      streetNormalized: comparable.streetNormalized,
      streetSource: comparable.streetSource,
      streetId: comparable.streetId
    }), comparable),
    {
      decision: comparable.decision,
      rejectionReason: comparable.rejectionReason,
      origin: comparable.origin,
      marketRole: comparable.marketRole ?? "primary",
      marketContextGroup: comparable.marketContextGroup,
      primaryQualificationReasons: comparable.primaryQualificationReasons
    }
  ),
  source_hash: comparable.sourceHash,
  similarity_score: comparable.similarityScore,
  score_breakdown: comparable.scoreBreakdown,
  created_at: comparable.createdAt,
  updated_at: comparable.updatedAt
});

const domainComparable = (row: MarketComparableRow) => ({
  id: row.id,
  analysisId: row.analysis_id,
  userId: row.user_id,
  sortOrder: row.sort_order,
  city: row.city,
  ...locationMetadataFromSourceSnapshot(row.source_snapshot, row.city, row.street),
  ...marketMetadataFromSourceSnapshot(row.source_snapshot),
  ...propertyModelFromSourceSnapshot(row.source_snapshot),
  locality: row.locality,
  street: row.street,
  propertyType: row.property_type,
  rooms: row.rooms,
  area: row.area ?? undefined,
  condition: row.condition,
  reconstruction: row.reconstruction,
  electricity: row.electricity,
  coreType: row.core_type,
  kitchen: row.kitchen,
  balconyType: row.balcony_type,
  cellar: row.cellar,
  floor: row.floor ?? undefined,
  elevator: row.elevator,
  orientation: row.orientation,
  buildingCondition: row.building_condition,
  askingPrice: row.asking_price ?? undefined,
  realizedPrice: row.realized_price ?? undefined,
  listingStatus: row.listing_status,
  sourceName: row.source_name,
  sourceUrl: row.source_url,
  capturedAt: row.captured_at,
  sourceSnapshot: sourceSnapshotWithoutLocationMetadata(row.source_snapshot),
  sourceHash: row.source_hash,
  similarityScore: row.similarity_score,
  scoreBreakdown: row.score_breakdown,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

const domainAnalysis = (row: MarketAnalysisRow, comparables: MarketComparableRow[]) => ({
  id: row.id,
  userId: row.user_id,
  caseId: row.case_id,
  version: row.version,
  status: row.status,
  subjectSnapshot: row.subject_snapshot,
  resultSnapshot: row.result_snapshot,
  marketFunnelSnapshot: row.market_funnel_snapshot,
  strategySnapshot: row.strategy_snapshot,
  methodologyVersion: row.methodology_version,
  sourceCutoffAt: row.source_cutoff_at,
  snapshotHash: row.snapshot_hash ?? undefined,
  comparables: comparables.filter((comparable) => comparable.analysis_id === row.id).map(domainComparable),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  finalizedAt: row.finalized_at ?? undefined
});

const fetchCloudAnalyses = async (initialSession: SupabaseSession) => {
  const analysisResult = await supabaseRequest<MarketAnalysisRow[]>(
    `/rest/v1/market_analyses?select=*&user_id=eq.${encodeURIComponent(initialSession.user.id)}&order=created_at.asc`,
    initialSession,
    { headers: { Range: "0-9999" } }
  );
  const comparableResult = await supabaseRequest<MarketComparableRow[]>(
    `/rest/v1/market_analysis_comparables?select=*&user_id=eq.${encodeURIComponent(initialSession.user.id)}&order=sort_order.asc`,
    analysisResult.session,
    { headers: { Range: "0-9999" } }
  );

  return {
    analyses: normalizeMarketAnalyses(
      analysisResult.data.map((row) => domainAnalysis(row, comparableResult.data))
    ),
    session: comparableResult.session
  };
};

const upsertAnalysis = async (
  analysis: MarketAnalysis,
  session: SupabaseSession,
  statusOverride?: "draft" | "finalized"
) => {
  const result = await supabaseRequest<void>("/rest/v1/market_analyses?on_conflict=id", session, {
    method: "POST",
    headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
    body: JSON.stringify(analysisRow(analysis, statusOverride))
  });
  return result.session;
};

const replaceDraftComparables = async (
  analysis: MarketAnalysis,
  cloudComparables: MarketAnalysisComparable[],
  initialSession: SupabaseSession
) => {
  let session = initialSession;
  const localIds = new Set(analysis.comparables.map((comparable) => comparable.id));
  for (const comparable of cloudComparables) {
    if (!localIds.has(comparable.id)) {
      const deletion = await supabaseRequest<void>(
        `/rest/v1/market_analysis_comparables?id=eq.${encodeURIComponent(comparable.id)}&analysis_id=eq.${encodeURIComponent(analysis.id)}`,
        session,
        { method: "DELETE", headers: { Prefer: "return=minimal" } }
      );
      session = deletion.session;
    }
  }

  if (analysis.comparables.length > 0) {
    const result = await supabaseRequest<void>(
      "/rest/v1/market_analysis_comparables?on_conflict=id",
      session,
      {
        method: "POST",
        headers: { Prefer: "resolution=merge-duplicates,return=minimal" },
        body: JSON.stringify(analysis.comparables.map(comparableRow))
      }
    );
    session = result.session;
  }
  return session;
};

const uploadAnalysis = async (
  analysis: MarketAnalysis,
  cloudAnalysis: MarketAnalysis | undefined,
  initialSession: SupabaseSession
) => {
  let session = initialSession;
  if (analysis.status === "finalized" && cloudAnalysis?.status !== "finalized") {
    session = await upsertAnalysis(analysis, session, "draft");
    session = await replaceDraftComparables(analysis, cloudAnalysis?.comparables ?? [], session);
    return upsertAnalysis(analysis, session, "finalized");
  }

  session = await upsertAnalysis(analysis, session);
  return replaceDraftComparables(analysis, cloudAnalysis?.comparables ?? [], session);
};

export const deleteCloudMarketAnalyses = async (session: SupabaseSession) => {
  const result = await supabaseRequest<void>(
    `/rest/v1/market_analyses?user_id=eq.${encodeURIComponent(session.user.id)}`,
    session,
    { method: "DELETE", headers: { Prefer: "return=minimal" } }
  );
  return result.session;
};

export const syncMarketAnalysesWithSupabase = async (
  localAnalyses: MarketAnalysis[],
  initialSession: SupabaseSession
): Promise<MarketAnalysisSyncResult> => {
  let session = initialSession;
  const localForUser = localAnalyses
    .filter((analysis) => analysis.userId === "local" || analysis.userId === session.user.id)
    .map((analysis) => ({
      ...analysis,
      userId: session.user.id,
      comparables: analysis.comparables.map((comparable) => ({ ...comparable, userId: session.user.id }))
    }));
  const cloudResult = await fetchCloudAnalyses(session);
  session = cloudResult.session;
  const reservedVersions = new Map<string, Set<number>>();
  cloudResult.analyses.forEach((analysis) => {
    const versions = reservedVersions.get(analysis.caseId) ?? new Set<number>();
    versions.add(analysis.version);
    reservedVersions.set(analysis.caseId, versions);
  });
  const cloudIds = new Set(cloudResult.analyses.map((analysis) => analysis.id));
  const ownedLocal = localForUser
    .sort((first, second) => timestamp(first.createdAt) - timestamp(second.createdAt))
    .map((analysis) => {
      const versions = reservedVersions.get(analysis.caseId) ?? new Set<number>();
      let version = analysis.version;
      if (!cloudIds.has(analysis.id) && versions.has(version)) {
        version = Math.max(0, ...versions) + 1;
      }
      versions.add(version);
      reservedVersions.set(analysis.caseId, versions);
      return version === analysis.version ? analysis : { ...analysis, version };
    });
  const cloudById = new Map(cloudResult.analyses.map((analysis) => [analysis.id, analysis]));
  const mergedById = new Map(ownedLocal.map((analysis) => [analysis.id, analysis]));

  for (const cloudAnalysis of cloudResult.analyses) {
    const localAnalysis = mergedById.get(cloudAnalysis.id);
    if (!localAnalysis) {
      mergedById.set(cloudAnalysis.id, cloudAnalysis);
      continue;
    }
    if (cloudAnalysis.status === "finalized") {
      mergedById.set(cloudAnalysis.id, cloudAnalysis);
      continue;
    }
    if (localAnalysis.status !== "finalized" && timestamp(cloudAnalysis.updatedAt) > timestamp(localAnalysis.updatedAt)) {
      mergedById.set(cloudAnalysis.id, cloudAnalysis);
    }
  }

  const merged = [...mergedById.values()].sort((first, second) => timestamp(first.createdAt) - timestamp(second.createdAt));
  for (const analysis of merged) {
    const cloudAnalysis = cloudById.get(analysis.id);
    const shouldUpload = !cloudAnalysis ||
      (analysis.status === "finalized" && cloudAnalysis.status === "draft") ||
      (analysis.status === "draft" && cloudAnalysis.status === "draft" && timestamp(analysis.updatedAt) > timestamp(cloudAnalysis.updatedAt));
    if (shouldUpload) {
      session = await uploadAnalysis(analysis, cloudAnalysis, session);
    }
  }

  return { analyses: merged, session };
};

export const marketAnalysisCollectionsMatch = (first: MarketAnalysis[], second: MarketAnalysis[]) =>
  JSON.stringify(first) === JSON.stringify(second);
